import { useEffect, useRef, useState } from "react";
import { getOwnSurveySettings, setOwnExtraSurveyWaves } from "../../../api/analyticsApi";

import { isOverviewAccessRefusal } from "../overviewAccess";

interface Props { actorId: string; actorScope: string; organisationId: string; allowed: boolean; onMutationStateChange: (pending: boolean) => void; onPublicationChange: () => void }
interface State { key: string; value: boolean | null; collectionEnabled: boolean; busy: boolean; error: string | null; saved: boolean; unavailable: boolean }

/** Own settings stay reachable when the aggregate publication is withheld. */
export default function ExtraSurveyWavesSettings({ actorId, actorScope, organisationId, allowed, onMutationStateChange, onPublicationChange }: Props) {
  const [retry, setRetry] = useState(0);
  const key = JSON.stringify([actorId, actorScope, organisationId, allowed, retry]);
  const currentKey = useRef(key);
  currentKey.current = key;
  const mounted = useRef(true);
  const [state, setState] = useState<State>({ key: "", value: null, collectionEnabled: false, busy: true, error: null, saved: false, unavailable: false });
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    const scope = JSON.stringify([actorId, actorScope, organisationId, allowed, retry]);
    setState({ key: scope, value: null, collectionEnabled: false, busy: true, error: null, saved: false, unavailable: false });
    getOwnSurveySettings().then(settings => {
      if (cancelled) return;
      if (settings === null) {
        setState({ key: scope, value: null, collectionEnabled: false, busy: false, error: null, saved: false, unavailable: true });
        return;
      }
      if (settings.actorId !== actorId || settings.organisationId !== organisationId || typeof settings.extraWaves !== "boolean") { onPublicationChange(); throw new Error("Your school settings changed. Reload before changing this preference."); }
      setState({ key: scope, value: settings.extraWaves, collectionEnabled: settings.enabled, busy: false, error: null, saved: false, unavailable: false });
    }).catch(error => {
      if (!cancelled) {
        setState({ key: scope, value: null, collectionEnabled: false, busy: false, error: error instanceof Error ? error.message : "Survey settings could not be verified.", saved: false, unavailable: false });
        if (isOverviewAccessRefusal(error)) onPublicationChange();
      }
    });
    return () => { cancelled = true; };
  }, [actorId, actorScope, organisationId, allowed, retry, onPublicationChange]);

  if (!allowed) return null;
  const current = state.key === key ? state : null;
  const save = async (next: boolean) => {
    const requestKey = key;
    const stillCurrent = () => mounted.current && currentKey.current === requestKey;
    if (!current || current.busy || current.value === null) return;
    setState({ ...current, busy: true, error: null, saved: false });
    onMutationStateChange(true);
    try {
      await setOwnExtraSurveyWaves(organisationId, next);
      if (!stillCurrent()) return;
      const saved = await getOwnSurveySettings();
      if (!stillCurrent()) return;
      if (saved === null || saved.actorId !== actorId || saved.organisationId !== organisationId || saved.extraWaves !== next) throw new Error("The saved preference could not be verified. Check it again before continuing.");
      setState({ key: requestKey, value: saved.extraWaves, collectionEnabled: saved.enabled, busy: false, error: null, saved: true, unavailable: false });
    } catch (error) {
      if (stillCurrent()) setState({ key: requestKey, value: null, collectionEnabled: false, busy: false, error: error instanceof Error ? error.message : "Survey settings could not be saved.", saved: false, unavailable: false });
    } finally {
      if (stillCurrent()) onMutationStateChange(false);
    }
  };
  return <div className="border-t border-pd-rule pt-4 text-sm text-pd-ink">
    <h3 className="font-semibold">Survey settings</h3>
    {!current || (current.busy && current.value === null) ? <p role="status" className="mt-2 text-pd-slate">Loading survey settings…</p> : null}
    {current?.value !== null && current?.value !== undefined ? <>
      <label className="mt-3 flex items-start gap-3"><input type="checkbox" checked={current.value} disabled={current.busy} onChange={event => { void save(event.target.checked); }} className="mt-1 accent-pd-ink" /><span>12-month survey</span></label>
      <p className="mt-2 text-xs text-pd-slate">Three- and six-month surveys are included for every school.</p>
      {!current.collectionEnabled ? <p className="mt-2 text-xs text-pd-slate">Collection is currently off. This preference is saved for when staff surveys are enabled.</p> : null}
    </> : null}
    {current?.unavailable ? <p role="status" className="mt-2 text-pd-slate">12-month survey settings are not available yet.</p> : null}
    {current?.error ? <div className="mt-3"><p role="alert">{current.error}</p><button type="button" onClick={() => setRetry(value => value + 1)} className="mt-2 rounded border border-pd-rule bg-pd-white px-3 py-2 font-medium">Check settings again</button></div> : null}
    {current?.saved ? <p role="status" className="mt-2">12-month survey preference saved.</p> : null}
    <p className="mt-3 text-xs text-pd-slate">After a change, analytics figures return at the next daily update.</p>
  </div>;
}
